//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IState } from 'angular-ui-router';
import kebabCase from 'lodash/fp/kebabCase';
import { INgRedux } from 'ng-redux';
import oc from 'oclazyload';

import { isDefined } from '@utils/filterPredicates';

import { FeatureViewNames } from 'app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import {
  defaultState,
  loggedOutDefaultState,
  syndicationDefaultState,
} from 'app/top-level-states';

export interface IStateWithData extends IState {
  data: IStateData;
}

export interface INamedStateWithData extends IStateWithData {
  name: string;
}
export interface INamedState extends IState {
  name: string;
}

export interface IPartialState extends IState {
  data?: Partial<IStateData>;
}

export const UniversalStateParams = {
  // This allows us to support anchor scrolling on any route
  scrollTo: null,
  viaTopNav: null,
};

function noopTrue() {
  return true;
}

export function StateFactory(state: IStateWithData): IStateWithData {
  const { allowLoggedOut, allowSyndicationClients, hasAccess } = state.data;
  const originalRedirectTo = state.redirectTo;
  return {
    params: UniversalStateParams,
    ...state,
    data: {
      ...state.data,
      // Fill in hasAccess if none is defined.
      hasAccess: hasAccess || noopTrue,
    },
    redirectTo(...args: unknown[]) {
      const session = crl8.session;
      if (!allowLoggedOut && !isAuthedSession(session)) {
        return loggedOutDefaultState.name;
      }
      if (
        !allowSyndicationClients &&
        isAuthedSession(session) &&
        session.client.isSyndication
      ) {
        return syndicationDefaultState.name;
      }
      if (hasAccess && !hasAccess()) {
        return defaultState.name;
      } else if (originalRedirectTo) {
        return typeof originalRedirectTo === 'function'
          ? (originalRedirectTo(...args) as string)
          : originalRedirectTo;
      }
    },
  };
}

type TitleFactory = () => string;

/**
 * Custom Curalate webapp properties for states.
 */
export interface IStateData {
  /**
   * Allow logged out users to access this state. (default: false)
   */
  allowLoggedOut?: boolean;
  /**
   * Allow Syndication-only clients to access this state. (default: false)
   */
  allowSyndicationClients?: boolean;
  /**
   * Page title to use for the tab/window title.
   */
  title: string | TitleFactory;
  /**
   * Which Nav Group does this state belong to?
   *
   * This is used for highlighting the correct top nav tab when the state is active.
   */
  navGroup: (() => NavGroup) | NavGroup;
  /**
   * Function to check if the user has access to this state.
   */
  hasAccess?: () => boolean;
}

export enum NavGroup {
  ADS = 'ads',
  AGENTS = 'agents',
  CAMPAIGNS = 'campaigns',
  CATALOG = 'catalog',
  COMMERCE = 'commerce',
  COMMUNITY = 'community',
  EXPORT = 'export',
  GALLERIES = 'galleries',
  HOME = 'home',
  INFLUENCERS = 'influencers',
  IMAGE_INSIGHTS = 'image-insights',
  INSIGHTS = 'insights',
  LISTENING = 'listening',
  MEDIA = 'media',
  PUBLISHING = 'publishing',
  SAMPLING = 'sampling',
  SCHEDULE = 'schedule',
  SOCIAL = 'social',
  SOCIAL_INBOX = 'social-inbox',
}

export const hasFeature = (feature: FeatureNames) =>
  !!crl8.session?.features[feature];
export const hasAllFeatures = (...features: FeatureNames[]) =>
  features.every((feature) => hasFeature(feature));
export const hasSomeFeatures = (...features: FeatureNames[]) =>
  features.some((feature) => hasFeature(feature));

export const isInDevMode = () => {
  return (
    isAuthedSession(crl8.session) &&
    crl8.session.internalInfo.featureViewName === FeatureViewNames.dev
  );
};

// Build the final set of modules we will tell ocLazyLoad to load.
function buildModules(moduleName: string | string[]): string[] {
  const modules = new Set<string>();
  // Always reload the curalate module.
  modules.add('curalate');
  if (Array.isArray(moduleName)) {
    moduleName.forEach((module) => modules.add(module));
  } else {
    modules.add(moduleName);
  }
  return Array.from(modules);
}

export function lazyLoadModule(
  moduleName: string | string[],
  importFn: () => Promise<unknown>,
  initRedux?: ($ngRedux: INgRedux) => void,
) {
  return {
    curalateBundle: [
      '$ngRedux',
      '$ocLazyLoad',
      ($ngRedux: INgRedux, $ocLazyLoad: oc.ILazyLoad) => {
        // inject --> hotswap or [re]load
        // ocLazyLoad will find new services, components registered to the given module, and load them
        // into the running angular application.
        return importFn().then(() => {
          $ocLazyLoad.inject(buildModules(moduleName));
          if (isDefined(initRedux)) {
            // TODO: PD-256663: Move galleries.routes.ts related nav components to styled RouteAnchors that allow us to pass the necessary params
            initRedux($ngRedux);
          }
        });
      },
    ],
  };
}

/**
 * Lazy load a bundle on a child state of a state that lazy loads a bundle.
 * @param moduleName Module name(s) to reload
 * @param importFn function returning a dynamic import promise.
 */
export function lazyLoadSubModule(
  moduleName: string | string[],
  importFn: () => Promise<unknown>,
) {
  // Require that the main bundle be loaded first.
  return {
    curalateSubBundle: [
      '$ocLazyLoad',
      'curalateBundle',
      ($ocLazyLoad: oc.ILazyLoad) => {
        return importFn().then(() =>
          $ocLazyLoad.inject(buildModules(moduleName)),
        );
      },
    ],
  };
}
export const componentNameToTemplate = (elementName: string) => {
  return `<${kebabCase(
    elementName,
  )} class="flex-tall cu-w-100" cu-page-scroll-to-top/>`;
};

/**
 * Create a string of optional query parameters for Angular state factory.
 * @param params array of query parameters
 */
export const makeStateFactoryQueryParams = (params: string[]): string => {
  return params.map((param) => `:${param}`).join('&');
};
